import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Layout as AntLayout, Menu, Button, Dropdown, Avatar, Space, Drawer, Select, message } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  HomeOutlined,
  BookOutlined,
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { textbookVersionAPI, textbookAPI, unitAPI, systemAPI } from '../../services/api';
import './Layout.css';

// 导入图片资源
import logoImage from '../../static/image/大鱼人机口语3x.png';
import vocabIcon from '../../static/image/同步词汇.png';
import textIcon from '../../static/image/同步课文.png';
import exerciseIcon from '../../static/image/课堂练习.png';
import soloIcon from '../../static/image/词汇对决.png';
import resourceIcon from '../../static/image/资源拓展.png';
import menuIcon from '../../static/image/菜单.png';
import collapseIcon from '../../static/image/收起.png';

const { Header, Sider, Content } = AntLayout;

const Layout = React.memo(({ children }) => {
  const [collapsed, setCollapsed] = useState(true); // 默认收起
  const [isMobile, setIsMobile] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false); // 防止重复初始化
  const navigate = useNavigate();
  const location = useLocation();

  // 教材选择相关状态
  const [versions, setVersions] = useState([]);
  const [textbooks, setTextbooks] = useState([]);
  const [units, setUnits] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [selectedTextbook, setSelectedTextbook] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [loading, setLoading] = useState({
    versions: false,
    textbooks: false,
    units: false
  });

  // 检测是否处于词汇选择模式（从比赛设置跳转过来的）
  const [isWordSelectionMode, setIsWordSelectionMode] = useState(false);

  // 菜单图片点击计数器（隐藏入口）
  const menuClickCountRef = React.useRef(0);
  const menuClickTimerRef = React.useRef(null);

  // 菜单图片点击处理（10次跳转到统计页面）
  const handleMenuIconClick = useCallback(() => {
    menuClickCountRef.current += 1;
    const newCount = menuClickCountRef.current;

    // 清除之前的定时器
    if (menuClickTimerRef.current) {
      clearTimeout(menuClickTimerRef.current);
    }

    if (newCount === 10) {
      // 点击10次，跳转到统计页面
      message.success('🎉 进入统计页面');
      navigate('/user-stats');
      menuClickCountRef.current = 0; // 重置计数器
    } else if (newCount >= 5) {
      // 提示还需要几次
      message.info(`再点击 ${10 - newCount} 次进入统计页面`);
    }

    // 5秒后重置计数器
    menuClickTimerRef.current = setTimeout(() => {
      menuClickCountRef.current = 0;
    }, 5000);
  }, [navigate]);

  // 监听词汇选择模式状态变化
  useEffect(() => {
    const checkWordSelectionMode = () => {
      const mode = localStorage.getItem('wordSelectionMode');
      setIsWordSelectionMode(mode === 'quiz');
    };

    // 初始检查
    checkWordSelectionMode();

    // 监听 storage 事件（从其他标签页或窗口的变化）
    const handleStorageChange = (e) => {
      if (e.key === 'wordSelectionMode') {
        checkWordSelectionMode();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // 由于同一个标签页的 localStorage 变化不会触发 storage 事件
    // 我们需要定期检查或使用其他方式
    const intervalId = setInterval(checkWordSelectionMode, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(intervalId);
    };
  }, []);

  // 清理菜单点击定时器
  useEffect(() => {
    return () => {
      if (menuClickTimerRef.current) {
        clearTimeout(menuClickTimerRef.current);
      }
    };
  }, []);

  // 检测屏幕尺寸 - 优化防抖
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // 防抖优化
    let timeoutId;
    const debouncedCheckScreenSize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkScreenSize, 150);
    };

    checkScreenSize();
    window.addEventListener('resize', debouncedCheckScreenSize);
    return () => {
      window.removeEventListener('resize', debouncedCheckScreenSize);
      clearTimeout(timeoutId);
    };
  }, []);

  // 初始化数据 - 防止重复初始化
  useEffect(() => {
    if (isInitialized) return;
    initializeData();
    setIsInitialized(true);
  }, []);

  // 🔧 监听路由变化，上报模块访问
  useEffect(() => {
    // 路径与模块名称的映射表
    const moduleMap = {
      '/word': '同步词汇',
      '/text': '同步课文',
      '/exercises': '课堂练习',
      '/solo': '词汇对决',
      '/good-reading': '好篇阅读',
      '/premium-teaching': '培优精讲',
      '/special-breakthrough': '专项突破',
      '/exam-strategy': '备考攻略',
      '/resource-download': '资源下载',
      '/dictation': '听写模式',
      '/reading': '领读模式',
      '/quantity-game': '题量赛',
      '/timed-game': '计时赛',
      '/rush-answer-game': '抢答赛',
    };

    const currentPath = location.pathname;
    const moduleName = moduleMap[currentPath];

    // 只上报已定义的模块
    if (moduleName) {
      systemAPI.trackModuleVisit(moduleName, currentPath);
    }
  }, [location.pathname]); // 监听路由变化

  // 🔧 监听用户活跃状态和页面关闭
  useEffect(() => {
    // 30分钟无操作超时时间
    const TIMEOUT_DURATION = 30 * 60 * 1000;
    let idleTimer;

    // 重置定时器
    const resetIdleTimer = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        systemAPI.reportModuleEnd();
      }, TIMEOUT_DURATION);
    };

    // 监听用户活动事件
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    
    // 简单的防抖处理，避免过于频繁重置
    let lastResetTime = 0;
    const handleUserActivity = () => {
      const now = Date.now();
      if (now - lastResetTime > 2000) { // 每2秒最多重置一次，减少性能开销
        resetIdleTimer();
        lastResetTime = now;
      }
    };

    activityEvents.forEach(event => {
      window.addEventListener(event, handleUserActivity);
    });

    // 初始化定时器
    resetIdleTimer();

    // 监听页面关闭/刷新
    const handleBeforeUnload = () => {
      systemAPI.reportModuleEnd();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      // 清理监听器和定时器
      if (idleTimer) clearTimeout(idleTimer);
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // 加载完整的选项数据（用于从localStorage恢复时）
  const loadCompleteOptionsData = async (savedSelection) => {
    try {
      // 1. 加载所有版本
      setLoading(prev => ({ ...prev, versions: true }));
      const versionsResponse = await textbookVersionAPI.getAll();
      const versionsData = versionsResponse.data || versionsResponse;
      setVersions(versionsData);
      
      // 2. 加载当前版本的所有教材
      setLoading(prev => ({ ...prev, textbooks: true }));
      const textbooksResponse = await textbookVersionAPI.getTextbooks(savedSelection.version.id);
      const textbooksData = textbooksResponse.data || textbooksResponse;
      setTextbooks(textbooksData);
      
      // 3. 加载当前教材的所有单元
      setLoading(prev => ({ ...prev, units: true }));
      const unitsResponse = await textbookAPI.getUnits(savedSelection.textbook.id);
      const unitsData = unitsResponse.data || unitsResponse;
      setUnits(unitsData);
      
    } catch (error) {
      console.error('加载完整选项数据失败:', error);
      message.error('加载完整数据失败，下拉框可能显示不完整');
      
      // 出错时至少保证当前选择的项目在列表中
      setVersions([savedSelection.version]);
      setTextbooks([savedSelection.textbook]);
      setUnits([savedSelection.unit]);
    } finally {
      setLoading({
        versions: false,
        textbooks: false,
        units: false
      });
    }
  };

  // 初始化数据函数
  const initializeData = async () => {
    try {
      // 从localStorage获取已保存的选择
      const savedData = localStorage.getItem('selectedTextbook');
      let savedSelection = null;
      if (savedData) {
        try {
          savedSelection = JSON.parse(savedData);
          // 验证数据完整性
          if (savedSelection && savedSelection.version && savedSelection.textbook && savedSelection.unit) {
            // 如果localStorage中有完整数据，仍需要加载完整的下拉框选项数据
            setSelectedVersion(savedSelection.version);
            setSelectedTextbook(savedSelection.textbook);
            setSelectedUnit(savedSelection.unit);
            
            // 加载完整的版本、教材、单元选项数据
            await loadCompleteOptionsData(savedSelection);
            
            // 触发自定义事件，通知其他组件单元已变化
            const unitChangeEvent = new CustomEvent('unitChanged', {
              detail: { unitId: savedSelection.unit.id, unit: savedSelection.unit }
            });
            window.dispatchEvent(unitChangeEvent);
            return; // 返回，因为已经加载了完整数据
          }
        } catch (error) {
          console.error('解析localStorage数据失败:', error);
          localStorage.removeItem('selectedTextbook');
        }
      }

      // 只有在localStorage无完整数据时才请求API
      // 获取版本列表
      setLoading(prev => ({ ...prev, versions: true }));
      const versionsResponse = await textbookVersionAPI.getAll();
      const versionsData = versionsResponse.data || versionsResponse;
      setVersions(versionsData);
      
      if (versionsData.length > 0) {
        // 如果没有保存的数据，默认选择第一个版本
        const firstVersion = versionsData[0];
        setSelectedVersion(firstVersion);
        
        // 获取第一个版本的教材
        setLoading(prev => ({ ...prev, textbooks: true }));
        const textbooksResponse = await textbookVersionAPI.getTextbooks(firstVersion.id);
        const textbooksData = textbooksResponse.data || textbooksResponse;
        setTextbooks(textbooksData);
        
        if (textbooksData.length > 0) {
          const firstTextbook = textbooksData[0];
          setSelectedTextbook(firstTextbook);
          
          // 获取第一个教材的单元
          setLoading(prev => ({ ...prev, units: true }));
          const unitsResponse = await textbookAPI.getUnits(firstTextbook.id);
          const unitsData = unitsResponse.data || unitsResponse;
          setUnits(unitsData);
          
          if (unitsData.length > 0) {
            const firstUnit = unitsData[0];
            setSelectedUnit(firstUnit);
            
            // 触发自定义事件，通知其他组件单元已变化
            const unitChangeEvent = new CustomEvent('unitChanged', {
              detail: { unitId: firstUnit.id, unit: firstUnit }
            });
            window.dispatchEvent(unitChangeEvent);
          }
        }
      }
    } catch (error) {
      console.error('初始化数据失败:', error);
      message.error('加载数据失败，请检查网络连接');
      
      // 设置默认测试数据
      setVersions([
        { id: 1, title: '人教版', description: '人民教育出版社' },
      ]);
      setSelectedVersion({ id: 1, title: '人教版', description: '人民教育出版社' });
      
      setTextbooks([
        { id: 1, title: '七年级上册', grade: 3, term: 1, publish_id: 1 },
        { id: 2, title: '七年级下册', grade: 3, term: 2, publish_id: 1 }
      ]);
      setSelectedTextbook({ id: 1, title: '三年级上册', grade: 3, term: 1, publish_id: 1 });
      
      setUnits([
        { id: 1, title: 'Unit 1 Hello!', textbook_id: 1 },
        { id: 2, title: 'Unit 2 Colours', textbook_id: 1 }
      ]);
      setSelectedUnit({ id: 1, title: 'Unit 1 Hello!', textbook_id: 1 });
    } finally {
      setLoading({
        versions: false,
        textbooks: false,
        units: false
      });
    }
  };

  // 更新localStorage - 使用useCallback优化
  const updateLocalStorage = useCallback((version, textbook, unit) => {
    const selectedData = {
      version: {
        id: version.id,
        title: version.title,
        description: version.description
      },
      textbook: {
        id: textbook.id,
        title: textbook.title,
        grade: textbook.grade,
        term: textbook.term,
        publish_id: textbook.publish_id
      },
      unit: {
        id: unit.id,
        title: unit.title,
        textbook_id: unit.textbook_id
      },
      selectedAt: new Date().toISOString()
    };
    localStorage.setItem('selectedTextbook', JSON.stringify(selectedData));
    
    // 触发自定义事件，通知其他组件 selectedTextbook 已更新
    const customEvent = new CustomEvent('customStorageChange', {
      detail: { key: 'selectedTextbook', value: selectedData }
    });
    window.dispatchEvent(customEvent);
  }, []);

  // 处理版本选择变更 - 添加防抖优化
  const handleVersionChange = useCallback(async (versionId) => {
    try {
      const version = versions.find(v => v.id === versionId);
      if (!version) return;

      setSelectedVersion(version);
      setSelectedTextbook(null);
      setSelectedUnit(null);
      setTextbooks([]);
      setUnits([]);

      // 获取新的教材列表
      setLoading(prev => ({ ...prev, textbooks: true }));
      const textbooksResponse = await textbookVersionAPI.getTextbooks(versionId);
      const textbooksData = textbooksResponse.data || textbooksResponse;
      setTextbooks(textbooksData);

      if (textbooksData.length > 0) {
        const firstTextbook = textbooksData[0];
        setSelectedTextbook(firstTextbook);

        // 获取新的单元列表
        setLoading(prev => ({ ...prev, units: true }));
        const unitsResponse = await textbookAPI.getUnits(firstTextbook.id);
        const unitsData = unitsResponse.data || unitsResponse;
        setUnits(unitsData);

        if (unitsData.length > 0) {
          const firstUnit = unitsData[0];
          setSelectedUnit(firstUnit);
          updateLocalStorage(version, firstTextbook, firstUnit);
          
          // 🔧 切换版本导致单元变化时，清空Quiz模式的单词选择记忆
          const quizMode = localStorage.getItem('wordSelectionMode');
          if (quizMode === 'quiz') {
            localStorage.removeItem('selectedWordsForQuiz');
          }
          
          // 触发自定义事件，通知其他组件单元已变化
          const unitChangeEvent = new CustomEvent('unitChanged', {
            detail: { unitId: firstUnit.id, unit: firstUnit }
          });
          window.dispatchEvent(unitChangeEvent);
        }
      }
    } catch (error) {
      console.error('切换版本失败:', error);
      message.error('切换版本失败');
    } finally {
      setLoading(prev => ({ ...prev, textbooks: false, units: false }));
    }
  }, [versions, updateLocalStorage]);

  // 处理教材选择变更 - 添加useCallback优化
  const handleTextbookChange = useCallback(async (textbookId) => {
    try {
      const textbook = textbooks.find(t => t.id === textbookId);
      if (!textbook || !selectedVersion) return;

      setSelectedTextbook(textbook);
      setSelectedUnit(null);
      setUnits([]);

      // 获取新的单元列表
      setLoading(prev => ({ ...prev, units: true }));
      const unitsResponse = await textbookAPI.getUnits(textbookId);
      const unitsData = unitsResponse.data || unitsResponse;
      setUnits(unitsData);

      if (unitsData.length > 0) {
        const firstUnit = unitsData[0];
        setSelectedUnit(firstUnit);
        updateLocalStorage(selectedVersion, textbook, firstUnit);
        
        // 🔧 切换教材导致单元变化时，清空Quiz模式的单词选择记忆
        const quizMode = localStorage.getItem('wordSelectionMode');
        if (quizMode === 'quiz') {
          localStorage.removeItem('selectedWordsForQuiz');
        }
        
        // 触发自定义事件，通知其他组件单元已变化
        const unitChangeEvent = new CustomEvent('unitChanged', {
          detail: { unitId: firstUnit.id, unit: firstUnit }
        });
        window.dispatchEvent(unitChangeEvent);
      }
    } catch (error) {
      console.error('切换教材失败:', error);
      message.error('切换教材失败');
    } finally {
      setLoading(prev => ({ ...prev, units: false }));
    }
  }, [textbooks, selectedVersion, updateLocalStorage]);

  // 处理单元选择变更 - 添加useCallback优化
  const handleUnitChange = useCallback((unitId) => {
    const unit = units.find(u => u.id === unitId);
    if (!unit || !selectedVersion || !selectedTextbook) return;

    setSelectedUnit(unit);
    updateLocalStorage(selectedVersion, selectedTextbook, unit);
    
    // 🔧 切换单元时，清空Quiz模式的单词选择记忆
    const quizMode = localStorage.getItem('wordSelectionMode');
    if (quizMode === 'quiz') {
      localStorage.removeItem('selectedWordsForQuiz');
    }
    
    // 触发自定义事件，通知其他组件单元已变化
    const unitChangeEvent = new CustomEvent('unitChanged', {
      detail: { unitId: unit.id, unit: unit }
    });
    window.dispatchEvent(unitChangeEvent);
  }, [units, selectedVersion, selectedTextbook, updateLocalStorage]);

  // 侧边栏菜单项 - 使用useMemo优化（包含资源拓展）
  const menuItems = useMemo(() => [
    {
      key: '/word',
      icon: <img src={vocabIcon} alt="同步词汇" />,
      label: '同步词汇',
    },
    {
      key: '/text',
      icon: <img src={textIcon} alt="同步课文" />,
      label: '同步课文',
    },
    {
      key: '/exercises',
      icon: <img src={exerciseIcon} alt="课堂练习" />,
      label: '课堂练习',
    },
    {
      key: '/solo',
      icon: <img src={soloIcon} alt="词汇对决" />,
      label: '词汇对决',
    },
    {
      key: 'resource-extension',
      icon: <img src={resourceIcon} alt="资源拓展" />,
      label: '资源拓展',
      children: [
        { key: '/good-reading', label: '好篇阅读' },
        { key: '/premium-teaching', label: '培优精讲' },
        { key: '/special-breakthrough', label: '专项突破' },
        { key: '/exam-strategy', label: '备考攻略' },
        { key: '/resource-download', label: '资源下载' },
      ],
    },
  ], []);


  // 用户下拉菜单
  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人资料',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '设置',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: () => {
        localStorage.removeItem('token');
        navigate('/login');
      },
    },
  ];

  const handleMenuClick = useCallback(({ key }) => {
    navigate(key);
    // 移动端点击菜单后关闭抽屉
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  }, [navigate, isMobile]);

  const toggleSidebar = useCallback(() => {
    if (isMobile) {
      setMobileDrawerOpen(!mobileDrawerOpen);
    } else {
      setCollapsed(!collapsed);
    }
  }, [isMobile, mobileDrawerOpen, collapsed]);

  // 侧边栏内容 - 使用useMemo优化
  const sidebarContent = useMemo(() => (
    <div className="sidebar-content">
      <Menu
        theme="light"
        mode="inline"
        selectedKeys={[location.pathname]}
        items={menuItems}
        onClick={handleMenuClick}
        inlineCollapsed={collapsed && !isMobile}
        className="sidebar-menu"
      />
      
      {/* 弹性空白区域，推送底部内容到最下方 */}
      <div className="sidebar-spacer"></div>
      
      {/* 底部菜单区域 */}
      <div className="sidebar-bottom">
        <div className="menu-item-bottom">
          <img 
            src={menuIcon} 
            alt="菜单" 
            className="menu-icon" 
            onClick={handleMenuIconClick}
            style={{ cursor: 'pointer' }}
            title="菜单"
          />
          <span className="menu-label">菜单</span>
          <button 
            className="collapse-btn"
            onClick={toggleSidebar}
            title="收起侧边栏"
          >
            <img src={collapseIcon} alt="收起" className="collapse-icon" />
          </button>
        </div>
      </div>
    </div>
  ), [location.pathname, menuItems, handleMenuClick, collapsed, isMobile, toggleSidebar, handleMenuIconClick]);

  return (
    <AntLayout className="layout">
      {/* 桌面端毛玻璃遮罩层 */}
      {!isMobile && !collapsed && (
        <div 
          className={`sidebar-overlay ${!collapsed ? 'visible' : ''}`}
          onClick={() => setCollapsed(true)}
        />
      )}

      {/* 桌面端侧边栏 */}
      {!isMobile && (
        <>
          {/* 收起状态的长方形框 */}
          {collapsed && (
            <>
              <div 
                className="collapsed-sidebar-indicator"
                onClick={() => setCollapsed(false)}
              >
                <div className="indicator-content">
                  同步课堂
                </div>
              </div>
              
              {/* 新增的小按钮 */}
              <div 
                className="collapsed-sidebar-indicator-small"
                onClick={() => setCollapsed(!collapsed)}
              >
                <div className="indicator-content-small">
                  {'❯❯'}
                </div>
              </div>
            </>
          )}
          
          <Sider 
            trigger={null} 
            collapsible 
            collapsed={false}
            className={`sidebar ${!collapsed ? 'expanded' : ''}`}
            width={200}
            style={{ background: '#ffffff' }}
            collapsedWidth={0}
          >
            {sidebarContent}
          </Sider>
        </>
      )}

      {/* 移动端抽屉 */}
      {isMobile && (
        <Drawer
          title={null}
          placement="left"
          closable={false}
          onClose={() => setMobileDrawerOpen(false)}
          open={mobileDrawerOpen}
          bodyStyle={{ padding: 0, background: '#ffffff' }}
          width={200}
        >
          {sidebarContent}
        </Drawer>
      )}
      
      <AntLayout className={`site-layout ${isMobile ? 'mobile' : ''}`}>
        <Header className="site-layout-header">
          <div className="header-logo-section">
            
            <img 
              src={logoImage} 
              alt="大鱼人机口语" 
              className="logo-image"
            />
          </div>
          
          <div className="header-selectors">
            <Space size="large">
              {isWordSelectionMode && (
                <div className="word-selection-tip">
                  <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                    📝 PK模式
                  </span>
                </div>
              )}
              <div className="selector-item">
                <span className="selector-label">选择教材版本:</span>
                <Select
                  value={selectedVersion ? { value: selectedVersion.id, label: selectedVersion.title } : undefined}
                  onChange={(selected) => handleVersionChange(selected?.value || selected)}
                  loading={loading.versions}
                  placeholder="请选择版本"
                  style={{ width: 150 }}
                  size="small"
                  labelInValue
                  disabled={isWordSelectionMode}
                  options={versions.map(version => ({
                    value: version.id,
                    label: version.title
                  }))}
                />
              </div>
              
              <div className="selector-item">
                <span className="selector-label">选择教材:</span>
                <Select
                  value={selectedTextbook ? { value: selectedTextbook.id, label: selectedTextbook.title } : undefined}
                  onChange={(selected) => handleTextbookChange(selected?.value || selected)}
                  loading={loading.textbooks}
                  placeholder="请选择教材"
                  style={{ width: 200 }}
                  size="small"
                  disabled={!selectedVersion || isWordSelectionMode}
                  labelInValue
                  options={textbooks.map(textbook => ({
                    value: textbook.id,
                    label: textbook.title
                  }))}
                />
              </div>
              
              <div className="selector-item">
                <span className="selector-label">选择单元:</span>
                <Select
                  value={selectedUnit ? { value: selectedUnit.id, label: selectedUnit.title } : undefined}
                  onChange={(selected) => handleUnitChange(selected?.value || selected)}
                  loading={loading.units}
                  placeholder="请选择单元"
                  style={{ width: 180 }}
                  size="small"
                  disabled={!selectedTextbook || isWordSelectionMode}
                  labelInValue
                  options={units.map(unit => ({
                    value: unit.id,
                    label: unit.title
                  }))}
                />
              </div>
            </Space>
          </div>
        </Header>
        
        <Content className="site-layout-content">
          {children}
        </Content>
      </AntLayout>
    </AntLayout>
  );
});

export default Layout;
